import { ReactPortal, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { getScrollbarSize } from 'shared/helpers/getScrollbarSize'
import { Handler } from 'shared/helpers/typeHelper'
import { css, styled } from 'styled-components'

import Overlay from '../Overlay/Overlay'
import { useDrawerProvider } from './DrawerContext'
import { DrawerHeader } from './DrawerHeader'

function changePaddings(padding: number) {
  document.querySelectorAll<HTMLElement>('body, .mui-fixed').forEach((el) => {
    el.style.paddingRight = `${padding}px`
  })
}

export interface DrawerProps {
  'data-testid'?: string
  isOpen: boolean
  children: (wrapRef: HTMLDivElement | null, handleClose: () => void) => React.ReactNode
  onClose: Handler
  openLinkAddress?: string
  header?: React.ReactNode
  className?: string
  width?: number
  withOverlay?: boolean
  customCloseHandler?: (isFromCloseButton: boolean, close: () => void) => void
}

export function Drawer({
  'data-testid': dataTestId,
  isOpen: isInitiallyOpen,
  children,
  onClose,
  openLinkAddress,
  header,
  className,
  width,
  withOverlay,
  customCloseHandler,
}: DrawerProps): ReactPortal | null {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const { generateDrawerWrap, cleanUpDrawerWrap } = useDrawerProvider()
  const [transitionIn, setTransitionIn] = useState(false)
  const drawerElementRef = useRef<HTMLDivElement | null>(null)
  const [isDrawerReady, setIsDrawerReady] = useState(false)
  const [hasEverOpened, setHasEverOpened] = useState(false)

  useEffect(() => {
    if (isInitiallyOpen) {
      const timeout = setTimeout(() => {
        setIsOpen(true)
        if (document.body.scrollHeight > document.body.clientHeight) {
          changePaddings(getScrollbarSize())
        }
      }, 10)

      return () => clearTimeout(timeout)
    }
  }, [isInitiallyOpen])

  useEffect(() => {
    if (isOpen && !drawerElementRef.current) {
      const newDrawerElement = generateDrawerWrap()
      drawerElementRef.current = newDrawerElement
      setIsDrawerReady(true)

      // Skip animation only if initially open AND this is the first time opening
      if (isInitiallyOpen && !hasEverOpened) {
        setTransitionIn(true)
        setHasEverOpened(true)
      } else {
        // Use requestAnimationFrame to ensure DOM is ready before setting transition
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTransitionIn(true)
          })
        })
        setHasEverOpened(true)
      }
    }

    if (!isOpen && drawerElementRef.current) {
      setTransitionIn(false)
      setTimeout(() => {
        cleanUpDrawerWrap(drawerElementRef.current)
        drawerElementRef.current = null
        setIsDrawerReady(false)
      }, 300)
    }
    // only isOpen should control those
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isInitiallyOpen])

  function closeDrawer() {
    setIsOpen(false)
    setTimeout(() => {
      changePaddings(0)
      onClose()
    }, 400)
  }

  function handleClose(isFromCloseButton: boolean) {
    if (customCloseHandler) {
      customCloseHandler(isFromCloseButton, () => closeDrawer())
    } else {
      closeDrawer()
    }
  }

  if (!isInitiallyOpen || !drawerElementRef.current || !isDrawerReady) return null

  return ReactDOM.createPortal(
    <>
      <DrawerWrapper
        data-testid={dataTestId ?? 'drawer-wrapper'}
        $transitionIn={transitionIn}
        $width={width}
        className={className}
        ref={wrapRef}
      >
        <DrawerHeader onClose={() => handleClose(true)} openLinkAddress={openLinkAddress}>
          {header}
        </DrawerHeader>
        <DrawerContent className="drawer-content">
          {children(wrapRef.current, () => handleClose(false))}
        </DrawerContent>
      </DrawerWrapper>
      {withOverlay && <StyledOverlay onClick={() => handleClose(false)} />}
    </>,
    drawerElementRef.current
  )
}

const DrawerWrapper = styled.div<{
  $transitionIn?: boolean
  $positionExpandedUnderToolbar?: boolean
  $background?: string
  $width?: number
}>`
  position: fixed;
  z-index: 15;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: ${({ $width }) => $width ?? 705}px;
  transform: translateX(100%);
  transition: transform 0.3s;
  background: ${({ $background }) => $background ?? 'white'};
  box-shadow: 0 2px 12px 5px rgba(0, 0, 0, 0.06);
  overflow-y: auto;
  overflow-x: hidden;

  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto 1fr;
  box-sizing: border-box;
  grid-template-areas:
    'drawer-header'
    'drawer-content';

  ${(p) =>
    p.$transitionIn &&
    css`
      transform: translateX(0);
    `}

  height: 100%;

  .drawer-header {
    max-height: 120px !important;
  }
`

const DrawerContent = styled.div`
  grid-area: drawer-content;
`

const StyledOverlay = styled(Overlay)`
  background-color: rgba(0, 0, 0, 0.05);
`
